import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Image as ImageIcon, QrCode, Plus, Upload, Calendar, X } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { getClientDeposits, listBankAccounts, adminCreateRecharge, generateRechargeUploadQR, checkRechargeUploadStatus } from "@/lib/api";
import { getBillingWeek, isInWeek, buildWeekOptions } from "@/lib/billingWeek";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || window.location.origin;
const fmtMoney = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const proofUrl = (p) => (!p ? null : p.startsWith("http") ? p : `${BACKEND_URL}${p}`);

const statusMeta = {
  approved: { label: "Aprobada", cls: "bg-green-100 text-green-700" },
  pending: { label: "Pendiente", cls: "bg-yellow-100 text-yellow-700" },
  rejected: { label: "Rechazada", cls: "bg-red-100 text-red-700" },
};

/**
 * Reusable dialog that shows a client's recargas (with week filter + proof
 * photo), lets the admin create a manual recharge and offers a QR to upload the
 * proof from a phone. Reuses the Recargas module backend.
 */
export default function ClientRechargesDialog({ open, onClose, client }) {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weekFilter, setWeekFilter] = useState("all");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [photo, setPhoto] = useState(null); // proof lightbox url

  // Manual recharge form
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [bankAccountId, setBankAccountId] = useState("");
  const [proof, setProof] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  // QR upload
  const [showQR, setShowQR] = useState(false);
  const [qrImg, setQrImg] = useState("");
  const pollRef = useRef(null);

  const load = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const [depRes, baRes] = await Promise.all([
        getClientDeposits(client.id),
        listBankAccounts().catch(() => ({ data: { bank_accounts: [] } })),
      ]);
      setDeposits(depRes.data?.deposits || []);
      const active = (baRes.data?.bank_accounts || []).filter((a) => a.is_active !== false);
      setBankAccounts(active);
      if (active.length === 1) setBankAccountId(active[0].id);
    } catch {
      toast.error("Error al cargar recargas del cliente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { load(); setWeekFilter("all"); setShowForm(false); }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client?.id]);

  const weekOptions = buildWeekOptions(deposits);
  const selectedWeek = weekFilter === "all" ? null : weekOptions.find((w) => w.key === weekFilter);
  const visibleDeposits = deposits.filter((d) => isInWeek(d.created_at, selectedWeek));

  const resetForm = () => {
    setAmount(""); setDescription(""); setProof(null); setProofPreview(null);
    setBankAccountId(bankAccounts.length === 1 ? bankAccounts[0].id : "");
    if (fileRef.current) fileRef.current.value = "";
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setProof(f);
    setProofPreview(URL.createObjectURL(f));
  };

  const handleGenerateQR = async () => {
    try {
      const res = await generateRechargeUploadQR(client.id);
      const token = res.data.upload_token;
      const url = `${APP_URL}/upload-recharge/${token}`;
      const qr = await QRCode.toDataURL(url, { width: 300, margin: 2, color: { dark: "#2ECC71", light: "#ffffff" } });
      setQrImg(qr);
      setShowQR(true);
      pollRef.current = setInterval(async () => {
        try {
          const st = await checkRechargeUploadStatus(token);
          if (st.data.used && st.data.image_url) {
            clearInterval(pollRef.current);
            setShowQR(false);
            const full = `${BACKEND_URL}${st.data.image_url}`;
            setProofPreview(full);
            const blob = await (await fetch(full)).blob();
            setProof(new File([blob], "proof_qr.jpg", { type: blob.type }));
            toast.success("¡Imagen recibida desde el teléfono!");
          }
        } catch {
          clearInterval(pollRef.current);
        }
      }, 3000);
    } catch {
      toast.error("Error al generar código QR");
    }
  };

  const handleCreate = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Ingresa un monto válido"); return; }
    if (!proof) { toast.error("El comprobante es obligatorio"); return; }
    if (bankAccounts.length === 0) { toast.error("No hay cuentas bancarias activas"); return; }
    if (!bankAccountId) { toast.error("Selecciona la cuenta bancaria de destino"); return; }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("user_id", client.id);
      fd.append("amount", parseFloat(amount));
      fd.append("description", description || "Recarga creada por administrador");
      fd.append("proof", proof);
      fd.append("bank_account_id", bankAccountId);
      await adminCreateRecharge(fd);
      toast.success("Recarga creada exitosamente");
      resetForm();
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la recarga");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="client-recharges-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Recargas de {client?.name}
              {client?.customer_id && <Badge variant="outline" className="text-xs">{client.customer_id}</Badge>}
            </DialogTitle>
            <DialogDescription>Revisa las recargas, mira el comprobante o crea una recarga manual.</DialogDescription>
          </DialogHeader>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={weekFilter} onValueChange={setWeekFilter}>
              <SelectTrigger className="h-9 w-56 rounded-lg text-xs" data-testid="client-recharges-week-filter">
                <Calendar className="h-3.5 w-3.5 text-indigo-500 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="client-recharges-week-all">Todas las recargas</SelectItem>
                {weekOptions.map((w) => (
                  <SelectItem key={w.key} value={w.key}>Semana {w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={() => setShowForm((s) => !s)}
              className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
              data-testid="client-recharges-new-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Recarga manual
            </Button>
          </div>

          {/* Manual recharge form */}
          {showForm && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="client-recharge-form">
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="0.01" min="0" placeholder="Monto *" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="recharge-amount-input" />
                <Select value={bankAccountId} onValueChange={setBankAccountId}>
                  <SelectTrigger className="rounded-lg" data-testid="recharge-bank-select">
                    <SelectValue placeholder="Cuenta destino *" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.bank_name} · {a.account_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Descripción (opcional)" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="recharge-description-input" />
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="recharge-upload-file-btn">
                  <Upload className="h-4 w-4 mr-1" /> Subir comprobante
                </Button>
                <Button variant="outline" size="sm" onClick={handleGenerateQR} data-testid="recharge-qr-btn">
                  <QrCode className="h-4 w-4 mr-1" /> QR (desde celular)
                </Button>
                {proofPreview && (
                  <img src={proofPreview} alt="comprobante" className="h-10 w-10 rounded object-cover border cursor-pointer" onClick={() => setPhoto(proofPreview)} />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); resetForm(); }}>Cancelar</Button>
                <Button size="sm" disabled={creating} onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recharge-create-submit">
                  {creating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Creando...</> : "Crear recarga"}
                </Button>
              </div>
            </div>
          )}

          {/* Deposits list */}
          <ScrollArea className="flex-1 min-h-[200px] max-h-[48vh] pr-2">
            {loading ? (
              <div className="py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />Cargando…</div>
            ) : visibleDeposits.length === 0 ? (
              <p className="text-center text-slate-400 py-10">No hay recargas {selectedWeek ? "en esta semana" : ""}</p>
            ) : (
              <div className="space-y-2">
                {visibleDeposits.map((d) => {
                  const sm = statusMeta[d.status] || { label: d.status, cls: "bg-slate-100 text-slate-600" };
                  const url = proofUrl(d.proof_url);
                  return (
                    <div key={d.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 bg-white" data-testid={`client-recharge-row-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800">{fmtMoney(d.amount)}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${sm.cls}`}>{sm.label}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(d.created_at).toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" })}
                          {d.reference_number ? ` · Ref: ${d.reference_number}` : ""}
                        </div>
                      </div>
                      {url ? (
                        <button
                          onClick={() => setPhoto(url)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                          data-testid={`client-recharge-photo-${d.id}`}
                        >
                          <ImageIcon className="h-4 w-4" /> Ver foto
                        </button>
                      ) : (
                        <span className="text-xs text-slate-300">Sin foto</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proof photo lightbox */}
      <Dialog open={!!photo} onOpenChange={(o) => !o && setPhoto(null)}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="recharge-photo-dialog">
          <DialogHeader>
            <DialogTitle>Comprobante</DialogTitle>
          </DialogHeader>
          {photo && <img src={photo} alt="Comprobante" className="w-full rounded-lg max-h-[70vh] object-contain" />}
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={showQR} onOpenChange={(o) => { if (!o) { setShowQR(false); if (pollRef.current) clearInterval(pollRef.current); } }}>
        <DialogContent className="rounded-2xl max-w-sm text-center" data-testid="recharge-qr-dialog">
          <DialogHeader>
            <DialogTitle>Subir comprobante desde el celular</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {qrImg && <img src={qrImg} alt="QR" className="w-56 h-56" />}
            <p className="text-sm text-slate-500">Escanea el QR con tu celular para subir la foto del comprobante. Se cargará aquí automáticamente.</p>
            <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
